import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  AnimatedDialog,
  AnimatedDialogScrollView,
  AnimatedDialogView,
  CUSTOM_NAVIGATION_HEADER_HEIGHT,
  FocusScope,
  theme,
  useResponsive,
} from '@sg/garnish';
import { OrderTypeValues } from '@sg/shared-be-features-ordering';
import { LoyaltyRewardTerms } from '@sg/shared-fe-features-loyalty';

import { Loyalty } from '@order/features-loyalty';
import { Bag, adjustWantedTime } from '@order/features-ordering';
import {
  StripePaymentButton,
  StripePaymentElement,
  StripePaymentMethod,
  useNotifyStripeCancel,
  useNotifyStripeError,
  useStripeConfirmPayment,
  useStripePaymentElement,
  useStripePaymentMethod,
} from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import { useBraze } from '../../../../context/Braze';
import { useBagContent, useLoyaltyContent, useLoyaltyPointsRewards } from '../../../../hooks';
import { type useOrderState, useValidProducts } from '../../hooks';
import { formatTelemetryBag, ORDER_PLAYBACK_CONFIG } from '../../utils';
import { BagDeliveryPreferencesForm } from '../BagDeliveryPreferencesForm';
import { BagTipping } from '../BagTipping';
import { BagAnimatedDialogHeader } from './BagAnimatedDialogHeader';

/**
 * Animated dialog that shows up when choosing options in the bag.
 */
export const BagAnimatedDialog = (props: BagAnimatedDialogProps) => {
  const { orderState } = props;

  const { track } = useTelemetry();
  const { logCustomEvent } = useBraze();
  const { currentBreakpoint, minWidth, currentHeight, match } = useResponsive();
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isChangingTime,
    isChangingDeliveryPreferences,
    isSubmittingOrder,
    isApplyingGiftCard,
    isPerformingBagAction,
    isShowingCheckoutLedger,
    isShowingOrderPlayback,
    isShowingModal,
    isShowingNextDayOrderNotice,
    isChangingPaymentMethod,
    isShowingGiftCardForm,
    isShowingLoyaltyInfo,
    isShowingLoyaltyTerms,
    isPostOrder,
  } = orderState;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    orderId = '',
    wantedTime,
    selectedTip,
    availableTips,
    cart,
    paymentMethods,
    availableCredit,
    availablePoints,
    shouldUseCredit,
    shouldUseFlex,
    amountEligibleForFlex,
    flexPaymentMethod,
    rewards,
    rewardTermsBeingShown,
    total,
    amountPaidWithStripe,
    creditToBeApplied,
    stripeFailedToLoad,
    stripeIntentConfig,
    stripeElementConfig,
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderChannel,
    restaurantId = '',
    locationName = '',
    deliveryOrderDetail,
    deliveryPreferences,
    wantedTimes,
    ledger,
    lineItems,
    maxTipAmount,
  } = cart;

  const products = useValidProducts(lineItems);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    goBack,
    cancelRequest,
    cancelPreviousIntent,
    changeDeliveryPreferences,
    changeTime,
    changeTip,
    requestPaymentMethodChange,
    finishPaymentSelection,
    changeCreditUsage,
    changeFlexUsage,
    requestGiftCardForm,
    submitGiftCardForm,
    generateAwsWafTokenRef,
    flagFailureAsShown,
    failPayment,
    setConfirmStripe,
    startStripeConfirmation,
    startOrderPolling,
    requestOrderSubmission,
    requestOrderPlayback,
    bagTracking,
  } = orderState;

  const { trackInitiatedCheckout } = bagTracking;

  // ─── Contentful ──────────────────────────────────────────────────────

  const { bagDiscountBody: discountInfo, homeProgressBenefitsDisclaimer: benefitsDisclaimer } =
    useLoyaltyContent({
      availablePoints,
    });

  const {
    orderPlaybackTitle,
    orderPlaybackTitleNonCard,
    orderPlaybackFooterTitle,
    orderPlaybackFooterLoading,
    orderPlaybackFooterLoadingNonCard,
    orderPlaybackFooterCta,
    placeOrderCta,
    nextDayOrderWarningRegular,
    nextDayOrderWarningOutpost,
  } = useBagContent();

  // ─── Points Rewards ───────────────────────────────────────────────────────

  const { pointsRewards, isFetchingPointsRewards, refetchPointsRewards } =
    useLoyaltyPointsRewards();

  // ─── Rewards Terms ─────────────────────────────────────────────────────────

  const rewardBeingShownTerms = useMemo(
    () => rewards.find((reward) => reward.id === rewardTermsBeingShown),
    [rewardTermsBeingShown, rewards],
  );

  // ─── Animated Dialog Scrolling ────────────────────────────────────────────
  // We track how much the dialog was scrolled to animate the header opacity.

  const scrollOffsetSV = useSharedValue(0);
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollAmount = event.nativeEvent.contentOffset.y;

      // Ignore auto scroll due to the dialog header.
      if (scrollAmount === CUSTOM_NAVIGATION_HEADER_HEIGHT) return;

      scrollOffsetSV.value = scrollAmount;
    },
    [scrollOffsetSV],
  );

  const resetScrollOffset = useCallback(() => {
    scrollOffsetSV.value = 0;
  }, [scrollOffsetSV]);

  // ─── Height Without Disclaimers ───────────────────────────────────────────
  // This section measures the checkout layout without the disclaimers at the bottom.
  // This will be the max height of the animated dialog, so disclaimers aren't immediately shown.

  const ledgerDialogHeight = useSharedValue(0);
  const ledgerContentHeight = useSharedValue(0);
  const ledgerContentStyle = useAnimatedStyle(() => ({ maxHeight: ledgerContentHeight.value }), [ledgerContentHeight]); // prettier-ignore
  const measureCheckoutWithoutDisclaimers = useCallback(
    (event: LayoutChangeEvent) => {
      const layoutHeight = event.nativeEvent.layout.height;
      const withHeader = layoutHeight + theme.spacing['18'];

      ledgerDialogHeight.value = withHeader;
      ledgerContentHeight.value = layoutHeight;
    },
    [ledgerDialogHeight, ledgerContentHeight],
  );

  // ─── Scroll View ──────────────────────────────────────────────────────────

  const scrollViewProps = useMemo(
    () => ({
      contentContainerStyle: styles.dialogContentInnerContainer,
      automaticallyAdjustKeyboardInsets: true,
      stickyHeaderIndices: [0],
      onScroll: handleOnScroll,
    }),
    [handleOnScroll],
  );

  const scrollViewFullHeightProps = useMemo(
    () => ({
      style: styles.fullHeight,
      stickyHeaderIndices: undefined,
      automaticallyAdjustKeyboardInsets: false,
    }),
    [],
  );

  // ─── Localization ────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const dismissModal = formatMessage(messages.dismissModal);
  const nextDayOrderWarning =
    orderChannel === 'outpost' ? nextDayOrderWarningOutpost : nextDayOrderWarningRegular;

  // ─── Stripe ──────────────────────────────────────────────────────────

  const trackStripeUsage = useCallback(
    (payload: Record<string, unknown>) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );
  const notifyStripeError = useNotifyStripeError();
  const notifyStripeCancel = useNotifyStripeCancel();

  const stripeIntentConfigRef = useRef(stripeIntentConfig);
  const stripeElementConfigRef = useRef(stripeElementConfig);

  const {
    embeddedPaymentElementView,
    stripeError,
    isUpdatingTotal,
    isApplePay,
    isGooglePay,
    isCashApp,
    selectedPaymentMethodType,
    selectedPaymentMethod,
    selectedPaymentImage,
    selectedPaymentIcon,
    confirmStripe,
  } = useStripePaymentElement({
    total: amountPaidWithStripe,
    paymentMethods,
    stripeIntentConfigRef,
    stripeElementConfigRef,
  });

  useEffect(() => {
    if (stripeError) notifyStripeError(stripeError);
  }, [stripeError, notifyStripeError]);

  const onPaymentFailure = useCallback(
    (error: string) => {
      notifyStripeError(error, { canShowErrorMessage: true });
      cancelRequest(true);
      failPayment();
      flagFailureAsShown();
    },
    [failPayment, flagFailureAsShown, cancelRequest, notifyStripeError],
  );

  const onPaymentCanceled = useCallback(() => {
    notifyStripeCancel();
    cancelPreviousIntent();
    cancelRequest(true);
    failPayment();
    flagFailureAsShown();
  }, [failPayment, flagFailureAsShown, cancelRequest, cancelPreviousIntent, notifyStripeCancel]);

  const { confirmPayment } = useStripeConfirmPayment({
    orderId,
    orderType: OrderTypeValues.GravyOrder,
    total: amountPaidWithStripe,
    paymentMethods,
    selectedPaymentMethod,
    trackStripeUsage,
    startStripeConfirmation,
    confirmStripe,
    onOrderReadyForPayment: requestOrderSubmission,
    onIntentConfirmed: startOrderPolling,
    onPaymentFailure,
    onPaymentCanceled,
  });

  const isSubmissionDisabled = isPerformingBagAction || isUpdatingTotal || selectedTip > maxTipAmount; // prettier-ignore
  const isApplePayWeb = isApplePay && Platform.OS === 'web';
  const isGooglePayWeb = isGooglePay && Platform.OS === 'web';
  const isGooglePayAndroid = isGooglePay && Platform.OS === 'android';

  // This is called when the customer presses the place order button.
  const handlePlaceOrder = useCallback(() => {
    track('checkout.start', formatTelemetryBag(orderState, selectedPaymentMethodType)); // prettier-ignore
    logCustomEvent('bag.place-order.selected');

    trackInitiatedCheckout({
      price: convertPriceToFloat(total),
      orderType: orderChannel,
      productIds: (lineItems ?? []).map((item) => item.product?.id ?? '').join(','),
      quantity: (lineItems ?? []).reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    });

    // The Apple Pay and Google Pay user interaction on web cannot be delayed by order playback, so it skips it.
    if (isGooglePayWeb || isApplePayWeb) {
      trackStripeUsage({ event: 'confirm_with_wallet_web', selectedPaymentMethodType }); // prettier-ignore
      void confirmPayment();
      return;
    }

    // Cash App also skips order playback, going straight to the polling state.
    if (isCashApp) {
      trackStripeUsage({ event: 'confirm_with_cash_app', selectedPaymentMethodType }); // prettier-ignore
      void confirmPayment();
      return;
    }

    trackStripeUsage({ event: 'confirm_with_playback', selectedPaymentMethodType }); // prettier-ignore
    requestOrderPlayback();
  }, [
    track,
    orderState,
    logCustomEvent,
    lineItems,
    total,
    orderChannel,
    trackInitiatedCheckout,
    isApplePayWeb,
    isGooglePayWeb,
    isCashApp,
    trackStripeUsage,
    selectedPaymentMethodType,
    confirmPayment,
    requestOrderPlayback,
  ]);

  const { paymentMethod, paymentMethodIcon, hasNoPaymentMethod } = useStripePaymentMethod({
    hasStripeFailedToLoad: stripeFailedToLoad,
    paymentMethods,
    name: selectedPaymentMethod,
    imageBase64: selectedPaymentImage,
    icon: selectedPaymentIcon,
    isGooglePay: isGooglePay,
  });

  // ─── Post Order Playback Binding ───────────────────────────────────────────
  // These must be bound to the machine so they can be invoked after order playback.

  useEffect(() => {
    setConfirmStripe(confirmPayment);
  }, [confirmPayment, setConfirmStripe]);

  // ─── Order Playback ───────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'bag.order-playback.view',
    payload: { orderPlaybackConfig: ORDER_PLAYBACK_CONFIG, supportsAutoSubmitEvent: true },
    skip: !isShowingOrderPlayback || isSubmittingOrder || isPostOrder,
  });

  // ─── Auto Close Payment Selection ──────────────────────────────────────────
  // Whenever the payment method changes, send the finish payment selection event.
  // This event is only consumed if the state is changing payment method.

  useEffect(() => {
    if (!selectedPaymentMethod) return;
    finishPaymentSelection();
  }, [finishPaymentSelection, selectedPaymentMethod]);

  // ──────────────────────────────────────────────────────────────────────────

  const hasMaxHeight = isShowingCheckoutLedger && !isFlexEnabled;
  const hasFullHeight =
    (isShowingOrderPlayback && (currentBreakpoint.isXS || (minWidth.isSM && currentHeight.isSM))) ||
    (isShowingCheckoutLedger && isFlexEnabled);

  const animatedDialogStyle = hasFullHeight ? styles.fullContentMargin : styles.largerContentMargin;
  const safeAreaViewStyle = hasFullHeight ? styles.fullContentMargin : undefined;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedDialog
      show={isShowingModal}
      maxHeight={hasMaxHeight ? ledgerDialogHeight : undefined}
      dismissOverlayAccessibilityLabel={dismissModal}
      style={animatedDialogStyle}
      backgroundStyle={hasFullHeight ? styles.hideBorderRadius : undefined}
      onLayout={resetScrollOffset}
      dismissModal={cancelRequest}
    >
      <SafeAreaView style={safeAreaViewStyle} edges={isShowingModal ? ['bottom', 'top'] : []}>
        {/* Wanted time selection */}
        {isChangingTime && wantedTime ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              closeA11yLabel={formatMessage(messages.dismissTimeSelectionModal)} // prettier-ignore
            />

            <Bag.TimePicker
              wantedTime={wantedTime}
              wantedTimes={wantedTimes}
              changeTime={changeTime}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Delivery preferences form */}
        {isChangingDeliveryPreferences ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader orderState={orderState} scrollOffsetSV={scrollOffsetSV} />

            <BagDeliveryPreferencesForm
              deliveryOrderDetail={deliveryOrderDetail}
              deliveryPreferences={deliveryPreferences}
              onSubmit={changeDeliveryPreferences}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Stripe payment element, always mounted otherwise the SDK doesn't load. */}
        <FocusScope enabled lockFocus={isChangingPaymentMethod}>
          <ScrollView
            style={isChangingPaymentMethod ? styles.stripePaymentElement : styles.hiddenWrapper}
            pointerEvents={isChangingPaymentMethod ? 'auto' : 'none'}
            accessibilityElementsHidden={!isChangingPaymentMethod}
          >
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              show={isChangingPaymentMethod}
            />

            <StripePaymentElement
              show={isChangingPaymentMethod}
              hasStripeFailedToLoad={stripeFailedToLoad}
              embeddedPaymentElementView={embeddedPaymentElementView}
            >
              <Bag.PaymentMethodGiftCardCta onPress={requestGiftCardForm} />
            </StripePaymentElement>
          </ScrollView>
        </FocusScope>

        {/* Giftcard form */}
        {isShowingGiftCardForm ? (
          <AnimatedDialogScrollView
            {...scrollViewProps}
            {...Platform.select({ native: scrollViewFullHeightProps })}
          >
            <Bag.GiftCardForm
              generateAwsWafTokenRef={generateAwsWafTokenRef}
              isLoading={isApplyingGiftCard}
              onSubmit={submitGiftCardForm}
              onBack={goBack}
              onClose={cancelRequest}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty info */}
        {isShowingLoyaltyInfo ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader orderState={orderState} scrollOffsetSV={scrollOffsetSV} />

            <Loyalty.InfoBenefits
              outerContainerStyle={styles.loyaltyInfoBenefits}
              loyaltyInfoBenefits={pointsRewards}
              isLoading={isFetchingPointsRewards}
              disclaimer={benefitsDisclaimer}
              refetch={refetchPointsRewards}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty Terms */}
        {isShowingLoyaltyTerms && rewardBeingShownTerms ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader orderState={orderState} scrollOffsetSV={scrollOffsetSV} />

            <LoyaltyRewardTerms
              title={rewardBeingShownTerms.name}
              terms={rewardBeingShownTerms.terms}
              expirationDate={rewardBeingShownTerms.expirationDate}
              style={styles.termsContainer}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Next Day Order Notice */}
        {isShowingNextDayOrderNotice ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader orderState={orderState} scrollOffsetSV={scrollOffsetSV} />

            <Bag.NextDayOrderWarning message={nextDayOrderWarning} onClose={cancelRequest} />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Order Playback */}
        {isShowingOrderPlayback ? (
          <AnimatedDialogView
            style={[
              styles.fullHeight,
              match([
                styles.orderPlaybackXS,
                currentHeight.isSM ? styles.orderPlaybackSmallHeightSM : styles.orderPlaybackSM,
              ]),
            ]}
          >
            <Bag.OrderPlayback
              products={products}
              wantedTime={adjustWantedTime(wantedTime)}
              locationName={locationName}
              isWantedTimeAsap={orderState.isWantedTimeAsap}
              orderChannel={orderChannel}
              isLoading={isSubmittingOrder}
              isPostOrder={isPostOrder}
              orderPlaybackTitle={isCashApp ? orderPlaybackTitleNonCard : orderPlaybackTitle}
              orderPlaybackFooterTitle={orderPlaybackFooterTitle}
              orderPlaybackFooterLoading={
                isCashApp ? orderPlaybackFooterLoadingNonCard : orderPlaybackFooterLoading
              }
              orderPlaybackFooterCta={orderPlaybackFooterCta}
              orderPlaybackConfig={ORDER_PLAYBACK_CONFIG}
              goBack={cancelRequest}
            />
          </AnimatedDialogView>
        ) : null}

        {/* Checkout ledger v1 */}
        {isShowingCheckoutLedger && !isFlexEnabled ? (
          <AnimatedDialogScrollView {...scrollViewProps} style={ledgerContentStyle}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              show={isShowingCheckoutLedger}
            />

            <Bag.CheckoutContainer onLayout={measureCheckoutWithoutDisclaimers}>
              <StripePaymentMethod
                hasStripeFailedToLoad={stripeFailedToLoad}
                paymentMethods={paymentMethods}
                name={selectedPaymentMethod}
                imageBase64={selectedPaymentImage}
                icon={selectedPaymentIcon}
                isDisabled={isSubmittingOrder}
                isGooglePay={isGooglePay}
                onPress={requestPaymentMethodChange}
              />

              {availableCredit > 0 ? (
                <Bag.CreditToggle
                  shouldUseCredit={shouldUseCredit}
                  availableCredit={availableCredit}
                  appliedCredit={creditToBeApplied}
                  onChange={changeCreditUsage}
                />
              ) : null}

              <BagTipping
                restaurantId={restaurantId}
                orderChannel={orderChannel}
                selectedTip={selectedTip}
                availableTips={availableTips}
                maxTipAmount={maxTipAmount}
                isDisabled={isPerformingBagAction}
                onTipChange={changeTip}
              />

              <Bag.Ledger
                ledger={ledger}
                tip={selectedTip}
                maxTipAmount={maxTipAmount}
                discountInfo={discountInfo}
                availableCredit={availableCredit}
                shouldUseCredit={shouldUseCredit}
                amountEligibleForFlex={amountEligibleForFlex}
                shouldUseFlex={shouldUseFlex}
              />

              {isGooglePayAndroid ? (
                <StripePaymentButton isDisabled={isSubmissionDisabled} onPress={handlePlaceOrder} />
              ) : (
                <Bag.CheckoutCta
                  cta={placeOrderCta}
                  paymentMethodId={selectedPaymentMethod || paymentMethods[0]?.id}
                  isLoading={isSubmittingOrder}
                  isDisabled={isSubmissionDisabled}
                  onCheckout={handlePlaceOrder}
                />
              )}
            </Bag.CheckoutContainer>

            <Bag.Disclaimers palette="dark" orderChannel={orderChannel} />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Checkout ledger v2 */}
        {isShowingCheckoutLedger && isFlexEnabled ? (
          <>
            <AnimatedDialogScrollView
              automaticallyAdjustKeyboardInsets
              stickyHeaderIndices={[0]}
              contentContainerStyle={styles.checkoutLedgerScrollView}
              onScroll={handleOnScroll}
            >
              <BagAnimatedDialogHeader
                palette="white"
                orderState={orderState}
                scrollOffsetSV={scrollOffsetSV}
                show={isShowingCheckoutLedger}
              />

              <View style={styles.checkoutLedger}>
                <Bag.AnimatedDialogTitle
                  isFlexEnabled
                  isShowingCheckoutLedger
                  orderChannel={orderChannel}
                />

                <BagTipping
                  restaurantId={restaurantId}
                  orderChannel={orderChannel}
                  selectedTip={selectedTip}
                  availableTips={availableTips}
                  maxTipAmount={maxTipAmount}
                  isDisabled={isPerformingBagAction}
                  onTipChange={changeTip}
                />

                <Bag.PriceSummary
                  ledger={ledger}
                  amountEligibleForFlex={amountEligibleForFlex}
                  tip={selectedTip}
                  maxTipAmount={maxTipAmount}
                  discountInfo={discountInfo}
                  availableCredit={availableCredit}
                  shouldUseCredit={shouldUseCredit}
                  shouldUseFlex={shouldUseFlex}
                />

                <Bag.PaymentMethods
                  isDisabled={isPerformingBagAction}
                  ledger={ledger}
                  tip={selectedTip}
                  flexPaymentMethod={flexPaymentMethod}
                  shouldUseFlex={shouldUseFlex}
                  amountEligibleForFlex={amountEligibleForFlex}
                  onChangeFlexUsage={changeFlexUsage}
                  shouldUseCredit={shouldUseCredit}
                  availableCredit={availableCredit}
                  appliedCredit={creditToBeApplied}
                  onChangeCreditUsage={changeCreditUsage}
                  hasNoPaymentMethod={hasNoPaymentMethod}
                  stripePaymentMethod={paymentMethod}
                  stripePaymentMethodIcon={paymentMethodIcon}
                  stripePaymentMethodImage={selectedPaymentImage}
                  onPressStripePaymentMethod={requestPaymentMethodChange}
                />

                <Bag.Disclaimers palette="light" orderChannel={orderChannel} />
              </View>
            </AnimatedDialogScrollView>

            <Bag.CheckoutCta
              isFloating
              cta={placeOrderCta}
              paymentMethodId={selectedPaymentMethod || paymentMethods[0]?.id}
              isLoading={isSubmittingOrder}
              isDisabled={isSubmissionDisabled}
              onCheckout={handlePlaceOrder}
            />
          </>
        ) : null}
      </SafeAreaView>
    </AnimatedDialog>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissTimeSelectionModal: {
    defaultMessage: 'Dismiss time selection modal',
    description: 'Bag | Animated Modal | Dismiss time selection modal',
  },
  dismissModal: {
    defaultMessage: 'Dismiss modal',
    description: 'Bag | Animated Modal | Dismiss modal',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  dialogContentInnerContainer: {
    flexGrow: 1,
  },
  fullContentMargin: {
    flex: 1,
    minHeight: 0,
    marginTop: 0,
  },
  largerContentMargin: {
    marginTop: CUSTOM_NAVIGATION_HEADER_HEIGHT,
  },
  loyaltyInfoBenefits: {
    marginBottom: theme.spacing['10'],
  },
  termsContainer: {
    marginTop: theme.spacing['6'],
  },
  orderPlaybackXS: {
    flexGrow: 1,
    height: '100%',
  },
  orderPlaybackSM: {
    flexShrink: 1,
    minHeight: 640,
  },
  orderPlaybackSmallHeightSM: {
    flexGrow: 1,
  },
  stripePaymentElement: {
    maxHeight: 500,
  },
  checkoutLedgerScrollView: {
    flexGrow: 1,
    backgroundColor: theme.colors.WHITE,
  },
  checkoutLedger: {
    gap: theme.spacing['4'],
    paddingTop: theme.spacing['5'],
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['24'],
  },
  hiddenWrapper: {
    flex: 0,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  fullHeight: {
    height: '100%',
  },
  hideBorderRadius: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagAnimatedDialogProps = {
  orderState: ReturnType<typeof useOrderState>;
};
